import React, { useState, useContext, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Slide,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { ReportContext } from "../../ReportContext";
import { AiOutlineFileAdd } from "react-icons/ai";

// Slide transition for the dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AddProposal = ({ solutionId, reload, setReload }) => {
  const { addProposals } = useContext(ReportContext);
  const [open, setOpen] = useState(false);
  const [proposal, setProposal] = useState("");
  const sessionKey = `proposal_${solutionId}`;
  const [submitting, setSubmitting] = useState(false);

  // Load saved proposal (if any)
  useEffect(() => {
    const saved = sessionStorage.getItem(sessionKey);
    if (saved) setProposal(saved);
  }, [sessionKey]);

  // Save proposal live as user types
  useEffect(() => {
    sessionStorage.setItem(sessionKey, proposal);
  }, [proposal, sessionKey]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (!proposal.trim()) return;

    setSubmitting(true);
    const response = await addProposals({
      solution_id: solutionId,
      notes: proposal,
    });

    if (response?.status === "success") {
      sessionStorage.removeItem(sessionKey);
      setProposal("");
      setReload(!reload);
      handleClose();
    }
    setSubmitting(false);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpen}
        startIcon={<AiOutlineFileAdd />}
        sx={{
          textTransform: "none",
          borderRadius: "10px",
          fontWeight: 600,
          px: 2.5,
          py: 1,
        }}
      >
        Add Proposal
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: 3,
            zIndex: 1400, // 🔝 Slightly higher z-index to overlay other dialogs
            p: 0,
          },
          component: motion.div,
          initial: { opacity: 0, scale: 0.9 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.9 },
          transition: { duration: 0.3 },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            borderBottom: "1px solid #e0e0e0",
            fontSize: "1.25rem",
          }}
        >
          Submit Proposal
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
            Your Proposal
          </Typography>
          <TextField
            multiline
            rows={8}
            fullWidth
            value={proposal}
            onChange={(e) => setProposal(e.target.value)}
            placeholder="Type your proposal here..."
            variant="outlined"
            sx={{
              background: "#fafafa",
              borderRadius: 2,
            }}
          />
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
            >
              Your progress is saved automatically.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #e0e0e0",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={handleClose}
            color="inherit"
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="success"
            variant="contained"
            disabled={!proposal.trim() || submitting}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
            }}
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddProposal;
